/**
 * Sync state in the header: when the mirror last ran, and a way to run it now.
 *
 * Worth being visible rather than buried in Settings — the app is a projection
 * of Todoist, so "how stale is this?" is a question you will actually have.
 */

import { SyncIcon } from "@/components/NavIcons";
import { sinceLabel, type AutoSyncState } from "@/hooks/useAutoSync";
import { appMode } from "@/lib/pocketbase";

export function SyncButton({
  state, compact = false, variant = "pill",
}: {
  state: AutoSyncState;
  compact?: boolean;
  /** "icon" is the phone header's 36pt circle, sized to be tapped. */
  variant?: "pill" | "icon";
}) {
  if (appMode === "demo") return null;
  const { syncing, lastSyncedAt, error, syncNow } = state;
  const icon = variant === "icon";

  return (
    <button
      type="button"
      onClick={() => void syncNow()}
      disabled={syncing}
      aria-label={syncing ? "Syncing" : "Sync with Todoist"}
      title={error ? `Last sync reported: ${error}` : `Todoist last pulled ${sinceLabel(lastSyncedAt)}`}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
        ...(icon
          ? { flex: "none", width: 36, height: 36, padding: 0, borderRadius: "50%", background: "var(--tint)", border: "none" }
          : { padding: compact ? "6px 9px" : "6px 12px", borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)" }),
        color: error ? "var(--warn)" : "var(--ink3)",
        fontSize: 12, cursor: syncing ? "default" : "pointer",
      }}
    >
      <span style={{ display: "flex", animation: syncing ? "ph-spin 1s linear infinite" : undefined }}>
        <SyncIcon size={icon ? 18 : 15} />
      </span>
      {!compact && !icon && <span>{syncing ? "Syncing…" : sinceLabel(lastSyncedAt)}</span>}
    </button>
  );
}
